/**
 * Subscription Status Display Component
 * 
 * Displays current subscription status with visual indicators and action buttons.
 * Based on the subscription-details.html mockup design.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useSubscription, useSubscriptionStatus, useSubscriptionActions } from '../hooks/useSubscription';
import SubscriptionActions from './SubscriptionActions';
import { SUBSCRIPTION_STATUS, SUBSCRIPTION_TYPES } from '../constants/subscriptionTypes';
import { useTranslation } from '../../i18n/hooks';
import './SubscriptionStatusDisplay.css';

/**
 * Subscription Status Display Component
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showActions - Whether to show action buttons
 * @param {Function} props.onActivate - Callback for activate action
 * @param {Function} props.onChangePlan - Callback for change plan action
 * @param {Function} props.onCancel - Callback for cancel action
 * @param {boolean} props.compact - Whether to show compact version
 * @returns {JSX.Element} Subscription status display component
 */
export const SubscriptionStatusDisplay = ({
  className = '',
  showActions = true,
  onActivate,
  onChangePlan,
  onCancel,
  compact = false
}) => {
  const { t, language } = useTranslation();
  const { 
    status, 
    type, 
    tier,
    expiresAt, 
    renewsAt, 
    loading 
  } = useSubscription();
  const isUltimate = tier === 'ULTIMATE';
  
  // Check if any loading operation is in progress
  const isLoading = loading.status || loading.activation || loading.deactivation || loading.renewal;
  
  const { 
    isActive, 
    isExpired, 
    isInactive, 
    needsRenewal, 
    isInGracePeriod, 
    statusLabel, 
    daysUntilExpiry 
  } = useSubscriptionStatus();
  
  const { activate, deactivate, cancel } = useSubscriptionActions();

  /**
   * Handle activate subscription
   */
  const handleActivate = async () => {
    try {
      await activate();
      if (onActivate) {
        onActivate();
      }
    } catch (error) {
      console.error('Failed to activate subscription:', error);
    }
  };

  /**
   * Handle change plan
   */
  const handleChangePlan = () => {
    if (onChangePlan) {
      onChangePlan();
    }
  };

  /**
   * Handle cancel subscription
   */
  const handleCancel = async () => {
    try {
      await cancel();
      if (onCancel) {
        onCancel();
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    }
  };

  /**
   * Get subscription type label
   */
  const getSubscriptionTypeLabel = () => {
    switch (type) {
      case SUBSCRIPTION_TYPES.BASIC:
        return t('subscription.types.basic');
      case SUBSCRIPTION_TYPES.PREMIUM:
        return t('subscription.types.premium');
      case SUBSCRIPTION_TYPES.ENTERPRISE:
        return t('subscription.types.enterprise');
      default:
        return t('subscription.types.none');
    }
  };

  /**
   * Get subscription price
   */
  const getSubscriptionPrice = () => {
    if (isUltimate) {
      return t('subscription.pricing.ultimate');
    }
    switch (type) {
      case SUBSCRIPTION_TYPES.BASIC:
        return t('subscription.pricing.basic');
      case SUBSCRIPTION_TYPES.PREMIUM:
        return t('subscription.pricing.premium');
      case SUBSCRIPTION_TYPES.ENTERPRISE:
        return t('subscription.pricing.enterprise');
      default:
        return t('subscription.pricing.none');
    }
  };

  const locale = language === 'bg' ? 'bg-BG' : 'en-US';

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return t('subscription.dates.notSet');
    
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return t('subscription.dates.invalid');
      }
      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return t('subscription.dates.invalid');
    }
  };

  /**
   * Format date and time (e.g. next billing instant)
   */
  const formatDateTime = (dateString) => {
    if (!dateString) return t('subscription.dates.notSet');

    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return t('subscription.dates.invalid');
      }
      return date.toLocaleString(locale, {
        dateStyle: 'long',
        timeStyle: 'short'
      });
    } catch (error) {
      return t('subscription.dates.invalid');
    }
  };

  /**
   * Get status indicator props
   */
  const getStatusIndicator = () => {
    if (isActive) {
      return {
        className: 'status-indicator status-active',
        icon: '●',
        text: t('subscription.status.active'),
        bgColor: 'bg-green-200',
        textColor: 'text-green-800',
        iconColor: 'text-green-500'
      };
    }
    
    if (isExpired) {
      return {
        className: 'status-indicator status-expired',
        icon: '●',
        text: t('subscription.status.expired'),
        bgColor: 'bg-red-200',
        textColor: 'text-red-800',
        iconColor: 'text-red-500'
      };
    }
    
    if (isInactive) {
      return {
        className: 'status-indicator status-inactive',
        icon: '●',
        text: t('subscription.status.inactive'),
        bgColor: 'bg-gray-600',
        textColor: 'text-gray-200',
        iconColor: 'text-gray-400'
      };
    }
    
    return {
      className: 'status-indicator status-none',
      icon: '●',
      text: t('subscription.status.none'),
      bgColor: 'bg-gray-600',
      textColor: 'text-gray-200',
      iconColor: 'text-gray-400'
    };
  };

  const statusIndicator = getStatusIndicator();

  if (isLoading) {
    return (
      <div className={`subscription-status-display loading ${className}`}>
        <div className="card">
          <div className="loading-skeleton">
            <div className="skeleton-header">
              <div className="skeleton-title"></div>
              <div className="skeleton-price"></div>
            </div>
            <div className="skeleton-content">
              <div className="skeleton-row"></div>
              <div className="skeleton-row"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`subscription-status-display ${compact ? 'compact' : ''} ${className}`}>
      <div className="card">
        <div className="subscription-header">
          <div className="subscription-info">
            <h2 className="subscription-title">
              {isUltimate ? t('subscription.titleUltimate') : t('subscription.title')}
            </h2>
          </div>
          <div className="subscription-pricing">
            {isActive && type && (
              <p className="subscription-price">
                {getSubscriptionPrice()}
              </p>
            )}
            <span className={`status-badge ${statusIndicator.bgColor} ${statusIndicator.textColor}`}>
              <svg 
                className={`status-icon ${statusIndicator.iconColor}`} 
                fill="currentColor" 
                viewBox="0 0 8 8"
              >
                <circle cx="4" cy="4" r="3"></circle>
              </svg>
              {statusIndicator.text}
            </span>
          </div>
        </div>

        <div className="subscription-details">
          <div className="details-grid">
            <div className="detail-item">
              <p className="detail-label">
                {t('subscription.labels.status')}
              </p>
              <p className="detail-value">
                {statusLabel}
              </p>
            </div>
            
            {expiresAt && isActive && (
              <div className="detail-item">
                <p className="detail-label">
                  {t('subscription.labels.expiryDate')}
                </p>
                <p className="detail-value">
                  {formatDate(expiresAt)}
                </p>
              </div>
            )}
            
            {renewsAt && isActive && (
              <div className="detail-item">
                <p className="detail-label">
                  {t('subscription.labels.nextPayment')}
                </p>
                <p className="detail-value">
                  {formatDateTime(renewsAt)}
                </p>
              </div>
            )}
            
            {isActive && daysUntilExpiry !== null && !isNaN(daysUntilExpiry) && (
              <div className="detail-item">
                <p className="detail-label">
                  {t('subscription.labels.daysRemaining')}
                </p>
                <p className={`detail-value ${daysUntilExpiry <= 7 ? 'warning' : ''}`}>
                  {daysUntilExpiry > 0 ? daysUntilExpiry : Math.abs(daysUntilExpiry)}
                  <span className="days-suffix">
                    {daysUntilExpiry > 0 ? t('subscription.days.remaining') : t('subscription.days.overdue')}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>

        {showActions && (
          <SubscriptionActions
            onActivate={onActivate}
            onCancel={onCancel}
            className="subscription-status-actions"
          />
        )}
      </div>
    </div>
  );
};

/**
 * PropTypes
 */
SubscriptionStatusDisplay.propTypes = {
  className: PropTypes.string,
  showActions: PropTypes.bool,
  onActivate: PropTypes.func,
  onChangePlan: PropTypes.func,
  onCancel: PropTypes.func,
  compact: PropTypes.bool
};

export default SubscriptionStatusDisplay;
